import { TimeRangeConfig, TimeRangeKind } from '@/api/generated/model';
import { WithDisabled, WithOnClick } from '@/types/component';
import { formatToLocaleDateValue, getTimezoneName } from '@/utils/date.util';
import { getDateTRangeFromKind, getDateTRangeFromKindWithoutCustom } from '@/utils/get-date-range-from-kind';
import { format } from 'date-fns';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { StableTimestamp } from '@/providers/stable-timestamp.provider';
import { getTimeRangeConfigDefault } from '@/utils/time-range-config';
import { Calendar } from '@/comps/primitives/calendar';
import { DateRange } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/comps/primitives/button';
import { TimePicker } from '@/comps/primitives/time-picker';
import { Skeleton } from '@/comps/primitives/skeleton';
import { OUTLINE_TOGGLE_CHIP_CLASSNAME } from '@/comps/primitives/outline-toggle-chip-styles';
import { ToggleGroup, ToggleGroupItem } from '@/comps/primitives/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/comps/primitives/select';
import { cn } from '@/utils/shad';
import { themeColors } from '@/theme/palette';

/** rgb(133, 134, 151) — themeColors.text.secondary / $text-secondary-color */
const TIME_RANGE_TEXT_COLOR = themeColors.text.secondary;
const timeRangeCellStyle = { color: TIME_RANGE_TEXT_COLOR };
/** rgb(39, 39, 42) — --border / --secondary / themeColors.text.muted / divider */
const timeRangeCellClassName = cn(
  'relative h-7 shrink-0 px-2 text-sm font-medium',
  OUTLINE_TOGGLE_CHIP_CLASSNAME,
);
const timeRangeToggleClassName = cn(timeRangeCellClassName, 'last:rounded-r-none');

const dropdownConfig: ToggleButtonConfig[] = [
  {
    value: TimeRangeKind.q1,
    children: 'First quarter',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind.q2,
    children: 'Second quarter',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind.q3,
    children: 'Third quarter',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind.q4,
    children: 'Fourth quarter',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind.this_month,
    children: 'This month',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind.this_quarter,
    children: 'This quarter',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind.this_year,
    children: 'This year',
    // ariaLabel: appendAdditionalToggles ? 'centered' : 'right aligned',
  },
  {
    value: TimeRangeKind.last_month,
    children: 'Last month',
    // ariaLabel: appendAdditionalToggles ? 'centered' : 'right aligned',
  },
  {
    value: TimeRangeKind.last_quarter,
    children: 'Last quarter',
    // ariaLabel: appendAdditionalToggles ? 'centered' : 'right aligned',
  },
  {
    value: TimeRangeKind['3_months_full'],
    children: 'Last 3 months',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind['6_months_full'],
    children: 'Last 6 months',
    ariaLabel: 'centered',
  },
  {
    value: TimeRangeKind['12_months_full'],
    children: 'Last 12 months',
    ariaLabel: 'centered',
  },
];

const staticToggleConfigs: ToggleButtonConfig[] = [
  { value: TimeRangeKind.today, children: 'Today', ariaLabel: 'centered' },
  { value: TimeRangeKind.yesterday, children: 'Yesterday', ariaLabel: 'centered' },
  { value: TimeRangeKind['7_days'], children: '7D', ariaLabel: 'centered' },
  { value: TimeRangeKind['30_days'], children: '30D', ariaLabel: 'centered' },
  { value: TimeRangeKind['3_months'], children: '3M', ariaLabel: 'centered' },
  { value: TimeRangeKind['6_months'], children: '6M', ariaLabel: 'centered' },
  { value: TimeRangeKind['12_months'], children: '12M', ariaLabel: 'centered' },
];

export const TIME_RANGE_LABELS: Partial<Record<string, string>> = Object.fromEntries(
  [...staticToggleConfigs, ...dropdownConfig].map((item) => [item.value, item.children as string]),
);

export type ReportTimeRangeKind = 'default';

export type ToggleButtonConfig = PropsWithChildren &
  WithOnClick & {
    value: TimeRangeKind | ReportTimeRangeKind;
    label?: string;
    ariaLabel?: string;
    disableRipple?: boolean;
  };

type Props = WithDisabled & {
  timeRange?: TimeRangeConfig;
  isLoading: boolean;
  setTimeRange: (timeRange: TimeRangeConfig | undefined) => void;
  appendAdditionalToggles?: ToggleButtonConfig[];
  prependAdditionalToggles?: ToggleButtonConfig[];
  overrideAlignment?: TimeRangeKind | ReportTimeRangeKind;
};

export const TimeRangeSelectPanel = ({
  timeRange,
  disabled,
  isLoading,
  setTimeRange,
  appendAdditionalToggles,
  prependAdditionalToggles,
  overrideAlignment,
}: Props) => {
  const { getTimestamp } = StableTimestamp.useProvided();
  const stableNow = getTimestamp();
  const alignment = overrideAlignment || timeRange?.kind || TimeRangeKind['30_days'];

  const {
    config,
    datePickerValue,
    setDatePickerValue,
    showDatePicker,
    setShowDatePicker,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    combineDateTime,
    alignTimeRangeToToday,
    lastCustomRange,
  } = useGeDefaultTimerangeTogglesConfig({
    timeRange,
    setTimeRange,
    stableNow,
  });

  const mergedConfig = useMemo(() => {
    const prepend = prependAdditionalToggles || [];
    const append = appendAdditionalToggles || [];

    return [...prepend, ...config, ...append];
  }, [config, appendAdditionalToggles, prependAdditionalToggles]);

  const dropDownElem = useMemo(
    () =>
      dropdownConfig.find((elem) => elem.value === timeRange?.kind)
        ? dropdownConfig.find((elem) => elem.value === timeRange?.kind)?.value
        : '',
    [timeRange?.kind],
  );

  // =====================================================
  // FUNCTIONS
  // =====================================================
  const updateDateRange = useCallback(
    (value: TimeRangeKind | ReportTimeRangeKind) => {
      if (value === 'default') {
        setTimeRange(undefined);
        return;
      }

      if (value === TimeRangeKind.custom) {
        const customRange = lastCustomRange || getTimeRangeConfigDefault();
        setTimeRange(customRange);
        return;
      }

      const { kind, start, end } = getDateTRangeFromKindWithoutCustom(value, stableNow);

      setTimeRange({
        kind,
        start,
        end,
        timezone: getTimezoneName(),
      });
    },
    [stableNow, setTimeRange, lastCustomRange],
  );

  const handleAlignment = useCallback(
    (newAlignment: TimeRangeKind | ReportTimeRangeKind | null) => {
      if (newAlignment === null || newAlignment === TimeRangeKind.custom) {
        return;
      }

      setDatePickerValue(undefined);
      setShowDatePicker(false);
      updateDateRange(newAlignment);
    },
    [setDatePickerValue, setShowDatePicker, updateDateRange],
  );

  const formatDateSelectString = useCallback((start: string, end?: string) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : stableNow;
    const monthStart =
      startDate.getMonth() !== endDate.getMonth() || startDate.getFullYear() !== endDate.getFullYear()
        ? startDate.toLocaleDateString('en-US', { month: 'short' })
        : '';
    const yearStart = startDate.getFullYear() !== endDate.getFullYear() ? startDate.getFullYear() : '';
    const monthEnd = endDate.toLocaleDateString('en-US', { month: 'short' });
    const yearEnd = endDate.getFullYear();

    return `${monthStart} ${yearStart} ${monthStart ? '- ' : ''}${monthEnd} ${yearEnd}`;
  }, [stableNow]);

  // =====================================================
  // JSX
  // =====================================================
  if (isLoading) {
    return (
      <div className="fadeIn flex items-end gap-2">
        <Skeleton className="h-8 w-[320px]" />
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <ToggleGroup
        type="single"
        layout="grouped"
        variant="outline"
        size="sm"
        value={alignment}
        onValueChange={(newAlignment) => {
          if (newAlignment) {
            handleAlignment(newAlignment as TimeRangeKind | ReportTimeRangeKind);
          }
        }}
        aria-label="text alignment"
        className="rounded-r-none"
      >
        {mergedConfig.map((item) => {
          if (item.value === 'custom') {
            return (
              <Popover key={item.value} open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <ToggleGroupItem
                    disabled={disabled}
                    value={item.value}
                    aria-label={item.ariaLabel}
                    onClick={item.onClick}
                    className={cn(timeRangeToggleClassName, 'gap-2', timeRange?.kind === 'custom' && 'bg-secondary!')}
                    style={timeRangeCellStyle}
                  >
                    {item.children}
                  </ToggleGroupItem>
                </PopoverTrigger>
                <PopoverContent className="w-fit max-w-none bg-popover! p-0 pt-1.5" align="start" side="bottom">
                  <div className="p-4">
                    <Calendar
                      initialFocus
                      mode="range"
                      selected={datePickerValue}
                      onSelect={(dateValue) => {
                        setDatePickerValue(dateValue);
                      }}
                      alignTimeRangeToToday={alignTimeRangeToToday}
                      numberOfMonths={2}
                    />

                    {/* Time selection section below calendar - always visible */}
                    <div className="mt-4 border-t pt-4">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-muted-foreground">Start time:</span>
                          <TimePicker value={startTime} onChange={(time) => setStartTime(time)} label="" />
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-muted-foreground">End time:</span>
                          <TimePicker value={endTime} onChange={(time) => setEndTime(time)} label="" />
                        </div>
                      </div>
                    </div>

                    {/* Apply/Cancel buttons */}
                    <div className="mt-4 flex justify-end gap-2 border-t pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDatePicker(false);
                          setDatePickerValue(undefined);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (datePickerValue?.from && datePickerValue?.to) {
                            const startDateTime = combineDateTime(datePickerValue.from, startTime);
                            const endDateTime = combineDateTime(datePickerValue.to, endTime);

                            setTimeRange({
                              kind: TimeRangeKind.custom,
                              start: formatToLocaleDateValue(startDateTime.valueOf()),
                              end: formatToLocaleDateValue(endDateTime.valueOf()),
                              timezone: getTimezoneName(),
                            });
                          } else if (datePickerValue?.from && datePickerValue?.syncToToday) {
                            const startDateTime = combineDateTime(datePickerValue.from, startTime);

                            setTimeRange({
                              kind: TimeRangeKind.custom,
                              start: formatToLocaleDateValue(startDateTime.valueOf()),
                              recompute: true,
                              timezone: getTimezoneName(),
                            });
                          }
                          setShowDatePicker(false);
                        }}
                        disabled={!datePickerValue?.from || (!datePickerValue?.to && !datePickerValue?.syncToToday)}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          }

          return (
            <ToggleGroupItem
              disabled={disabled}
              key={item.value}
              value={item.value}
              aria-label={item.ariaLabel}
              onClick={item.onClick}
              className={timeRangeToggleClassName}
              style={timeRangeCellStyle}
            >
              {item.children}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
      <Select
        disabled={disabled}
        value={dropDownElem || undefined}
        onValueChange={(value) => handleAlignment(value as TimeRangeKind)}
      >
        <SelectTrigger
          className={cn(
            timeRangeCellClassName,
            'w-auto min-w-7 justify-center gap-2 rounded-l-none rounded-r-md border-l-0 hover:border-l',
            dropDownElem && 'bg-secondary! text-white!',
          )}
          style={timeRangeCellStyle}
          showCaret
          chevronClassName="ml-0 text-current opacity-100"
        >
          {dropDownElem ? (
            <span className="font-medium">{dropdownConfig.find((elem) => elem.value === dropDownElem)?.children}</span>
          ) : null}
        </SelectTrigger>
        <SelectContent className="min-w-75">
          {dropdownConfig.map((item) => {
            if (item.value === 'default') return null;
            const tr =
              item.value === TimeRangeKind.custom
                ? getDateTRangeFromKind(timeRange ?? getTimeRangeConfigDefault(), stableNow)
                : getDateTRangeFromKindWithoutCustom(item.value as Exclude<TimeRangeKind, 'custom'>, stableNow);
            return (
              <SelectItem
                key={item.value}
                value={item.value}
                className="w-full pr-2 data-[state=checked]:bg-secondary [&>span.absolute]:hidden"
              >
                <p className="flex w-full items-center justify-between gap-6">
                  <span>{item.children}</span>
                  <span className="ml-auto shrink-0 text-neutral-500">{formatDateSelectString(tr.start, tr.end)}</span>
                </p>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <div className="grow" />
    </div>
  );
};

type GeDefaultTimerangeTogglesConfigParams = {
  timeRange?: TimeRangeConfig;
  setTimeRange: (timeRange: TimeRangeConfig | undefined) => void;
  stableNow?: Date;
};

type CustomDateRange = DateRange & {
  syncToToday?: boolean;
};

export function useGeDefaultTimerangeTogglesConfig(params: GeDefaultTimerangeTogglesConfigParams) {
  const { timeRange, setTimeRange, stableNow = new Date() } = params;

  const [datePickerValue, setDatePickerValue] = useState<CustomDateRange | undefined>(undefined);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [lastCustomRange, setLastCustomRange] = useState<TimeRangeConfig | undefined>(undefined);

  const combineDateTime = useCallback((date: Date, time: string): Date => {
    const [hours, minutes] = time.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      console.warn(`Invalid time string: ${time}, falling back to 00:00`);
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    }

    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }, []);

  const alignTimeRangeToToday = useCallback(
    (selectedDate: CustomDateRange | undefined) => {
      if (selectedDate?.from) {
        // Immediately create and apply the date_to_now range
        const startDateTime = combineDateTime(selectedDate.from, startTime);
        const endDateTime = combineDateTime(stableNow, '23:59');

        // Validate that start is before end
        if (startDateTime >= endDateTime) {
          console.warn('Invalid date range: start date must be before end date');
          return;
        }

        setTimeRange({
          start: formatToLocaleDateValue(startDateTime.valueOf()),
          end: formatToLocaleDateValue(endDateTime.valueOf()),
          timezone: getTimezoneName(),
          recompute: true,
          kind: TimeRangeKind.custom,
        });

        setShowDatePicker(false);
      }
    },
    [combineDateTime, stableNow, startTime, setTimeRange, setShowDatePicker],
  );

  useEffect(() => {
    if (timeRange?.kind === TimeRangeKind.custom && timeRange?.start && timeRange?.end) {
      setLastCustomRange(timeRange);

      const startDate = new Date(timeRange.start);
      const endDate = new Date(timeRange.end);

      const startTimeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
      const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

      setStartTime((prev) => (prev !== startTimeStr ? startTimeStr : prev));
      setEndTime((prev) => (prev !== endTimeStr ? endTimeStr : prev));
    } else if (timeRange?.kind === TimeRangeKind.custom && timeRange?.start) {
      setLastCustomRange(timeRange);
    }
  }, [timeRange]);

  useEffect(() => {
    if (timeRange?.kind === TimeRangeKind.custom && timeRange?.start && timeRange?.end) {
      const startLocal = new Date(timeRange.start);
      const endLocal = new Date(timeRange.end);

      setDatePickerValue({ from: new Date(startLocal), to: new Date(endLocal) });
    } else if (timeRange?.kind === TimeRangeKind.custom && timeRange?.start) {
      setDatePickerValue({ from: new Date(timeRange.start), syncToToday: true });
    }
  }, [timeRange]);

  const datePickerTitle = useMemo(() => {
    if (lastCustomRange && lastCustomRange.kind === 'custom') {
      const displayRange = lastCustomRange;

      if (!displayRange?.start || !displayRange?.end) {
        return 'Custom';
      }

      const formatDateTime = (dateStr: string, showTime: boolean) => {
        const date = new Date(dateStr);
        if (showTime) {
          return format(date, 'MMM dd, HH:mm');
        }
        return format(date, 'MMM dd');
      };

      if (displayRange.kind === 'custom' && displayRange.start && displayRange.end) {
        const showStartTime = true;
        const showEndTime = true;

        return `${formatDateTime(displayRange.start, showStartTime)} - ${formatDateTime(displayRange.end, showEndTime)}`;
      } else if (displayRange.kind === 'custom' && displayRange.start) {
        const showStartTime = true;

        const endDate = new Date(displayRange.end);
        const displayEndTime = `${format(endDate, 'MMM dd')}, 23:59`;

        return `${formatDateTime(displayRange.start, showStartTime)} - ${displayEndTime}`;
      }
    }
    return 'Custom';
  }, [lastCustomRange]);

  const config: ToggleButtonConfig[] = useMemo(
    () => [
      {
        value: TimeRangeKind.custom,
        disableRipple: true,
        children: (
          <>
            <CalendarDays className="size-4" />
            <span className="w-max">{datePickerTitle}</span>
          </>
        ),
      },
      ...staticToggleConfigs,
    ],
    [datePickerTitle],
  );

  return {
    config,
    datePickerValue,
    setDatePickerValue,
    showDatePicker,
    setShowDatePicker,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    combineDateTime,
    alignTimeRangeToToday,
    lastCustomRange,
  };
}
